/**
 * Camera feel (BUILD_PLAN section 7: recoil kick, impact shake, dynamic zoom) as a pure spring
 * and decay state. It produces offsets applied after the chase camera's base transform and never
 * feeds the simulation.
 */

export interface CameraFxState {
  /** Radians of upward kick still to unwind. */
  recoilPitch: number;
  recoilYaw: number;
  /** Shake amplitude in radians. */
  shake: number;
  shakePhase: number;
  /** 0 = normal field of view, 1 = fully zoomed. */
  zoom: number;
  /** Alternates the yaw kick direction. */
  kicks: number;
}

export interface CameraOffsets {
  pitch: number;
  yaw: number;
  roll: number;
  /** Multiplier on the field of view (zoom). */
  fovScale: number;
}

export const RECOIL_DECAY = 9;
export const SHAKE_DECAY = 6;
export const ZOOM_RATE = 8;
export const ZOOM_FOV_SCALE = 0.5;
const MAX_RECOIL = 0.12;
const MAX_SHAKE = 0.05;

export function initialCameraFx(): CameraFxState {
  return { recoilPitch: 0, recoilYaw: 0, shake: 0, shakePhase: 0, zoom: 0, kicks: 0 };
}

/** A weapon fired: pitch up by `degrees` with a small alternating yaw component. */
export function kickRecoil(s: CameraFxState, degrees: number): CameraFxState {
  const rad = (degrees * Math.PI) / 180;
  const side = s.kicks % 2 === 0 ? 1 : -1;
  return {
    ...s,
    recoilPitch: Math.min(MAX_RECOIL, s.recoilPitch + rad),
    recoilYaw: Math.max(-MAX_RECOIL, Math.min(MAX_RECOIL, s.recoilYaw + rad * 0.35 * side)),
    kicks: s.kicks + 1,
  };
}

/** Took a hit or stood near an explosion: shake scaled by the damage fraction (0..1). */
export function addShake(s: CameraFxState, amount: number): CameraFxState {
  return { ...s, shake: Math.min(MAX_SHAKE, s.shake + amount * MAX_SHAKE) };
}

export function stepCameraFx(s: CameraFxState, dt: number, zoomTarget: 0 | 1): CameraFxState {
  const decay = Math.exp(-RECOIL_DECAY * dt);
  const shakeDecay = Math.exp(-SHAKE_DECAY * dt);
  const zoomK = 1 - Math.exp(-ZOOM_RATE * dt);
  return {
    recoilPitch: s.recoilPitch * decay,
    recoilYaw: s.recoilYaw * decay,
    shake: s.shake * shakeDecay,
    shakePhase: s.shakePhase + dt * 41,
    zoom: s.zoom + (zoomTarget - s.zoom) * zoomK,
    kicks: s.kicks,
  };
}

export function cameraOffsets(s: CameraFxState): CameraOffsets {
  return {
    pitch: s.recoilPitch + Math.sin(s.shakePhase) * s.shake,
    yaw: s.recoilYaw + Math.sin(s.shakePhase * 1.31 + 1) * s.shake * 0.8,
    roll: Math.sin(s.shakePhase * 0.77 + 2) * s.shake * 0.5,
    fovScale: 1 - (1 - ZOOM_FOV_SCALE) * s.zoom,
  };
}
